(ns client-portal.client.core.data
  (:require [cljs-time.format :as f]
            [cljs-time.coerce :as coerce]
            [cljs-time.core :as t]
            [clojure.string :as s]
            [shared.core.reactive :as re]
            [shared.core.router :as router]
            [shared.core.time :as time]
            [shared.core.util :as util]))

(def root-path [:core])
(def sidebar-closed-path (conj root-path :sidebar-closed?))
(def manage-path (conj root-path :manage))
(def timeframe-path (conj manage-path :timeframe))
(def timeframe-date-range-path (conj manage-path :timeframe-date-range))
(def selected-crew-type-path (conj manage-path :selected-crew-type))
(def header-filters-path (conj root-path :header-filters))
(def ai-agent-path (conj root-path :ai-agent))
(def ai-agent-session-path (conj ai-agent-path :session))
(def agent-list-path (conj ai-agent-path :list))

(def most-recent-timeframe-value "most-recent")
(def default-timeframe-value 7)
(def crew-type-human "human")
(def date-formatter-yyyy-MM-dd (f/formatter "yyyy-MM-dd"))

(re/reg-sub
  :core/sidebar-closed?-
  (fn [db]
    (get-in (:local-storage db) sidebar-closed-path)))

(re/reg-event-fx
  :core/toggle-sidebar re/middleware
  (fn [{:keys [local-storage]}]
    {:local-storage (update-in local-storage sidebar-closed-path not)}))

(re/reg-event-fx
  :core/set-sidebar-closed re/middleware
  (fn [{:keys [local-storage]} [closed?]]
    {:local-storage (assoc-in local-storage sidebar-closed-path closed?)}))

(defn stored-header-filters [db]
  (get-in (:local-storage db) header-filters-path))

(re/reg-sub :core/stored-header-filters- stored-header-filters)

(def header-filter-keys [:cid :lid :ct :tf :start :end])

(def carried-query-param-keys
  "Query params kept from the current URL when the header's concept changes or a nav link is followed: the Voice
   Insights namespace/dataset and the products' BigQuery project overrides"
  [:ns :ds :dsid :oasys-proj :vc-proj])

(defn ->header-filters [query-params]
  (->> (select-keys query-params header-filter-keys)
       (remove (fn [[_ v]] (s/blank? (str v))))
       (into {})))

(re/reg-event-fx
  :core/store-header-filters re/middleware
  (fn [{:keys [local-storage]} [filters]]
    {:local-storage (assoc-in local-storage header-filters-path filters)}))

(re/reg-event-fx
  :core/close-sidebar re/middleware
  (fn [{:keys [local-storage]}]
    {:local-storage (assoc-in local-storage sidebar-closed-path true)}))

;; Sidebar nav-group expand/collapse — persisted so the tree state survives refresh.
;; Stores the set of collapsed groups, so a group defaults to expanded.

(def nav-group-collapsed-path (conj root-path :nav-group-collapsed))

(re/reg-sub
  :core/nav-group-expanded?-
  (fn [db [_ group]]
    (not (contains? (get-in (:local-storage db) nav-group-collapsed-path #{}) group))))

(re/reg-event-fx
  :core/toggle-nav-group re/middleware
  (fn [{:keys [local-storage]} [group]]
    (let [collapsed (get-in local-storage nav-group-collapsed-path #{})]
      {:local-storage (assoc-in local-storage nav-group-collapsed-path
                                (if (contains? collapsed group)
                                  (disj collapsed group)
                                  (conj collapsed group)))})))

(re/reg-event-db
  :core/check-for-route-change re/middleware
  (fn [db]
    (when (not= (router/current-route-name db) (some-> (router/previous-route db) (first)))
      (re/dispatch [:layout/hide-panel-right]))
    db))

(re/reg-event-fx
  :core/init-drawer-listener re/middleware
  (fn [_]
    {:forward-events {:register :core-drawer-initialization-listener
                      :events #{:router/go}
                      :dispatch-to [:core/check-for-route-change]}}))

;; Timeframe / crew-type filter state — shared by every application header.

(defn selected-crew-type [db] (get-in db selected-crew-type-path))
(defn selected-timeframe [db] (get-in db timeframe-path))
(defn selected-timeframe-date-range [db] (get-in db timeframe-date-range-path))

(defn tf->timeframe [tf]
  (when (not-empty tf)
    (try
      (let [tf-int (js/parseInt tf)]
        (if (= (str tf) (str tf-int))
          tf-int
          tf))
      (catch :default _e
        tf))))

(defn convert-date-range-time [in-time-s]
  (some-> in-time-s
          (coerce/from-string)
          (t/plus (t/seconds (inc (time/tz-local-offset-seconds "UTC"))))
          (coerce/to-date)))

(defn start-end-strings->date-range [start-s end-s]
  (when (not-empty start-s)
    [(convert-date-range-time start-s)
     (convert-date-range-time end-s)]))

(defn format-start-end-date [d]
  (some->> d
           (coerce/from-date)
           (f/unparse date-formatter-yyyy-MM-dd)))

(defn initialize-common-filters [db]
  (let [qps (router/current-query-params db)
        {:keys [ct tf start end]} (if (s/blank? (str (:cid qps)))
                                    (stored-header-filters db)
                                    qps)]
    (-> db
        (assoc-in selected-crew-type-path (or (not-empty ct) crew-type-human))
        (assoc-in timeframe-path (or (tf->timeframe tf) default-timeframe-value))
        (assoc-in timeframe-date-range-path (start-end-strings->date-range start end)))))

(defn timeframe-date-params
  "The header timeframe/datepicker selection as {:start-date :end-date} (yyyy-MM-dd)
   request params — empty when no concrete range applies (e.g. Most Recent, Live)."
  [db]
  (let [timeframe (selected-timeframe db)
        [start end] (selected-timeframe-date-range db)
        days-ago->date-string (fn [days-ago]
                                (f/unparse date-formatter-yyyy-MM-dd
                                           (t/minus (t/time-now) (t/days days-ago))))]
    (cond
      (= timeframe -1)
      (let [start-s (format-start-end-date start)
            ;; Add a day to the picked end so the range is inclusive of that day.
            end-s (some-> end
                          (coerce/from-date)
                          (t/plus (t/days 1))
                          (#(f/unparse date-formatter-yyyy-MM-dd %)))]
        (if (and (not-empty start-s) (not-empty end-s))
          {:start-date start-s
           :end-date end-s}
          {}))

      (and (number? timeframe) (>= timeframe 0))
      {:start-date (days-ago->date-string timeframe)
       :end-date (days-ago->date-string (if (= timeframe 1) 0 -1))}

      :else {})))

(re/reg-sub :core/selected-crew-type- selected-crew-type)
(re/reg-sub :core/selected-timeframe- selected-timeframe)
(re/reg-sub :core/selected-timeframe-date-range- selected-timeframe-date-range)

(re/reg-event-db
  :core/select-crew-type re/middleware
  (fn [db [crew-type]]
    (assoc-in db selected-crew-type-path crew-type)))

(re/reg-event-db
  :core/select-timeframe re/middleware
  (fn [db [timeframe]]
    (assoc-in db timeframe-path timeframe)))

(re/reg-event-db
  :core/select-timeframe-date-range re/middleware
  (fn [db [timeframe-date-range]]
    (assoc-in db timeframe-date-range-path timeframe-date-range)))

(re/reg-event-db
  :core/timeframe-date-range-changed re/middleware
  (fn [db _]
    db))

;; AI agent list + session — powers the header agent picker. The chat state under
;; ai-agent-path is populated by the ai-agent feature.
(re/reg-auth0-loader
  :core/load-ai-agent-list
  agent-list-path
  {:method :get
   :url "/api/ai-agent/list"}
  :transf (fn [resp] (or (some-> resp :body (util/->kebab-case-keywords))
                         []))
  :error-transf (fn [] []))

(re/reg-auth0-load-sub
  :core/ai-agent-list-
  agent-list-path
  :core/load-ai-agent-list)

(re/reg-sub
  :core/ai-agent-session-id-
  (fn [db]
    (get-in db (conj ai-agent-session-path :id))))

(re/reg-event-db
  :core/initialize-filters re/middleware
  (fn [db]
    (initialize-common-filters db)))
